/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RedoOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import Table from 'components/Table';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';

import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { checkToken } from 'api/utils';

import { getEstadosGlobales } from 'api/catalogos/index';

import {
  getBimestres,
  getBimestre,
  postBimestre,
  patchBimestre,
  deleteBimestre,
  generarBimestres,
} from 'api/nominas/bimestre';

import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';

export const bimestrePermission = {
  permissionModel: 'bimestre',
  catalogsModels: [],
  tabsModels: [],
};

const Bimestre = ({ permission }) => {
  const [form] = Form.useForm();
  const [formGenerar] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleGenerar, setVisibleGenerar] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);

  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getBimestres();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();

      const [
        _estados,
        _periodosFiscales,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getBimestres(),
      ]);

      if (mounted) {
        setEstados(_estados);
        setPeriodosFiscales(_periodosFiscales);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    form.resetFields();
    setSelectedRowKeys([]);
    setSelectedData({});
    setDisableFields(false);
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _registro = await getBimestre(key);

    if (_registro) {
      form.setFieldsValue(formatReceived(_registro));
      setDisableFields(_registro.estados_globales !== ESTATUS_ACTIVO);
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      const saved = key
        ? await patchBimestre(key, values)
        : await postBimestre(values);

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit();
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteBimestre(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const onConfirmAutorizar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await patchBimestre(key, { estados_globales: ESTATUS_AUTORIZADO });
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const onConfirmCancelar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await patchBimestre(key, { estados_globales: ESTATUS_CANCELADO });
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const onConfirmReactivar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const saved = await patchBimestre(key, { estados_globales: ESTATUS_ACTIVO });
    if (saved) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const onClickGenerar = () => {
    formGenerar.resetFields();
    setVisibleGenerar(true);
  };

  const onFinishGenerar = async () => {
    try {
      setLoading(true);
      await formGenerar.validateFields();
      const { periodo_fiscal: periodoFiscal } = formGenerar.getFieldsValue();

      const resultado = await generarBimestres(periodoFiscal);
      if (resultado) {
        await fetchData();
        setVisibleGenerar(false);
        formGenerar.resetFields();
        if (resultado.omitidos) {
          onSuccess(
            {},
            `Se generaron ${resultado.creados?.length || 0} Bimestres. `
            + `${resultado.omitidos} ya existían para ese Periodo Fiscal y se omitieron.`,
          );
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 120,
      render: (val) => periodosFiscales.find((e) => e.id === val)?.periodo || `ID ${val}`,
    },
    {
      titleText: 'Bimestre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 150,
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo_descripcion',
      key: 'periodo_descripcion',
      width: 200,
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'no_de_dias',
      key: 'no_de_dias',
      width: 120,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  return (
    <Row className="container">
      <Spin spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={{ type: 'radio', selectedRowKeys }}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: onClickGenerar,
              permission: { model: 'bimestre', action: 'add' },
              icon: <ThunderboltOutlined />,
              text: 'Generar Bimestres del Ejercicio',
              disabledBySelection: false,
              disabled: false,
            },
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'bimestre', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_AUTORIZADO
                || selectedData?.estados_globales === ESTATUS_CANCELADO,
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: 'bimestre', action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_CANCELADO,
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: 'bimestre', action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_ACTIVO,
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
        />

        <Modal
          open={open}
          closable={false}
          footer={null}
          onCancel={onCancel}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Bimestre`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="periodo_fiscal"
                label="Ejercicio"
                rules={[requiredRule]}
                hasFeedback
              >
                <Select
                  dataSource={periodosFiscales}
                  keyProp="id"
                  labelProp="periodo"
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="nombre"
                label="Nombre del Bimestre"
                rules={[requiredRule]}
                extra="Ejemplo: Bimestre 1"
              >
                <Input disabled={disableFields} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="periodo_descripcion"
                label="Periodo del Bimestre"
                rules={[requiredRule]}
                extra="Ejemplo: Enero - Febrero"
              >
                <Input disabled={disableFields} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="no_de_dias"
                label="No. de Días del Bimestre"
                extra="Se calcula automáticamente al guardar, según el Periodo del Bimestre"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item name="estados_globales" label="Estatus">
                <Select
                  dataSource={estados}
                  labelProp="descripcion"
                  keyProp="id"
                  disabled
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>

        <Modal
          open={visibleGenerar}
          closable={false}
          footer={null}
          onCancel={() => setVisibleGenerar(false)}
          title={(
            <FormSubmitControls
              label="Generar Bimestres del Ejercicio"
              onFinish={onFinishGenerar}
              onCancel={() => setVisibleGenerar(false)}
              loading={loading}
            />
          )}
        >
          <Form
            layout="vertical"
            form={formGenerar}
            onFinish={onFinishGenerar}
            scrollToFirstError
            name="generar"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="periodo_fiscal"
                label="Ejercicio"
                rules={[requiredRule]}
                hasFeedback
                extra="Se crearán los 6 Bimestres estándar (Bimestre 1 a 6, Enero-Febrero a Noviembre-Diciembre) para el Ejercicio seleccionado. Los que ya existan para ese Ejercicio se omiten."
              >
                <Select
                  dataSource={periodosFiscales}
                  keyProp="id"
                  labelProp="periodo"
                  allowClear
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>

        <ModalDelete
          open={visibleAlert}
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          loading={loading}
          content={`Bimestre ${selectedData?.nombre || ''}`}
        />

        <ModalConfirmation
          open={visibleAutorizar}
          onFinish={onConfirmAutorizar}
          onCancel={() => setVisibleAutorizar(false)}
          ConfirmAutorize
          contentTitle={`Autorizar el ${selectedData?.nombre || 'Bimestre'}`}
          loading={loading}
        />

        <ModalConfirmation
          open={visibleCancelar}
          onFinish={onConfirmCancelar}
          onCancel={() => setVisibleCancelar(false)}
          ConfirmCancel
          contentTitle={`Cancelar el ${selectedData?.nombre || 'Bimestre'}`}
          loading={loading}
        />

        <ModalConfirmation
          open={visibleReactivar}
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar el ${selectedData?.nombre || 'Bimestre'}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Bimestre;
